import { Schema, type, ArraySchema, MapSchema } from "@colyseus/schema";
import { BuffInstance } from "./buff-instance";

/**
 * Per-cube ability state — learned spells, Q/W/E/R bindings, cooldown
 * timers, in-flight cast staging, and the +pct mining-rate buff slot used
 * by Mineral Tap.
 *
 * Lives on Cube as `cube.spellbook` (NOT flat fields) for the same reason
 * `cube.resources` (ResourceBag) and `cube.survival` (SurvivalBag) do:
 * @colyseus/schema imposes a hard 64-field-per-class cap, and Cube is
 * already heavy with combat / derived-stat surface. Grouping the ability
 * surface costs 1 Cube field instead of 13.
 *
 * Mutation pattern is the same as the other bags — just one hop:
 *   cube.spellbook.slotQ = "MineralTap"
 *   cube.spellbook.cooldowns.set("MineralTap", now + 30000)
 *
 * Spec: docs/design/09-abilities.md §"Slots & binding", §"Cast flow
 * (server-authoritative)".
 *
 * Field roles (annotation contract, mirrors the ResourceBag / SurvivalBag
 * pattern in this codebase):
 *
 *   TRANSIENT — written by the cast pipeline / tick loop; clients read for
 *               UI feedback (cooldown wheels, cast bar). Cleared on death
 *               per §"Combat-resolver interactions" → "Death resets full
 *               caster state".
 *   STATEFUL  — long-lived bookkeeping (learned[], slot bindings,
 *               miningRateBp). Survives respawn; cleared only on tavern
 *               re-bind / buff expiry.
 *
 * `castStage` enum (mirrors §"Cast flow" steps):
 *   0 = idle (no in-flight cast)
 *   1 = walking-to-range (point / unit modes outside range)
 *   2 = casting (within range; effect about to fire — instant in v1, but
 *       reserved for future cast-time spells)
 *   3 = channeling (per-period tick-driven cast; Celestial Hymn customer)
 *
 * Channel state reuses the in-flight cast slots — when `castStage ===
 * Channeling`, `castAbilityId` carries the channeled ability id and
 * `castTarget*` carries the channel target (mutually exclusive with
 * Walking/Casting, so reusing the slots costs no schema fields). The two
 * channel-only fields below (`channelPeriodAccumMs`, `channelTicksRemaining`)
 * cover the per-tick bookkeeping that doesn't fit on the cast surface.
 *
 * Toggles are a parallel surface to the in-flight cast: a cube can have any
 * number of toggles ON simultaneously (Shadow Cloak, future Adrenaline Rush /
 * Sage Mode), independent of whether something is also walk-queuing or
 * channeling. Each entry in `toggles` carries the auto-off deadline (0 =
 * indefinite, drains until re-press / break event / death).
 *
 * Wire-format note: stage is a uint8 instead of a string to keep the delta
 * bandwidth small (cast stage may flip several times per second during
 * walk-to-range chains).
 */
export class Spellbook extends Schema {
  /**
   * STATEFUL — list of learned ability ids (matches `ABILITIES` keys).
   * Tavern purchase pushes; there is no unlearn in v1 (re-buy is the only
   * swap path per §"Slot management — tavern purchase + drag-replace").
   * For the v1 expertise utility kit, the first Mining/Forestry point
   * pushes MineralTap / NaturesGrace / PlantTree onto this list.
   */
  @type([ "string" ]) learned = new ArraySchema<string>();

  /**
   * STATEFUL — Q/W/E/R slot bindings. Empty string = unbound. Per §"Slots
   * & binding", the auxiliary toggle bar (AdrenalineRush / Sage Mode) is
   * SEPARATE from these 4 slots — those land on a future `aux*` field
   * when their tier-4 expertise unlocks ship.
   */
  @type("string") slotQ: string = "";
  @type("string") slotW: string = "";
  @type("string") slotE: string = "";
  @type("string") slotR: string = "";

  /**
   * TRANSIENT — per-ability cooldown deadlines. Key = abilityId; value =
   * server-ms timestamp the cooldown expires. A row absent from the map
   * means the ability is off cooldown. Cleared on death per §"Combat-
   * resolver interactions".
   *
   * (Kept as a MapSchema-of-number rather than a parallel ArraySchema to
   * keep the wire diff small — only the ability whose CD changed deltas;
   * the others stay quiet.)
   */
  @type({ map: "number" }) cooldowns = new MapSchema<number>();

  /**
   * TRANSIENT — in-flight cast state. The cast pipeline writes these on
   * `castIntent` arrival and clears them when the cast resolves /
   * cancels. The client uses them to render the cast bar, target reticle,
   * and walk-to-range path visualization.
   *
   * castStage encoding: 0 = idle, 1 = walking-to-range, 2 = casting,
   * 3 = channeling. See file-level docstring for the full description.
   */
  @type("uint8")  castStage: number = 0;
  @type("string") castAbilityId: string = "";
  @type("string") castTargetUnitId: string = "";
  @type("number") castTargetX: number = 0;
  @type("number") castTargetZ: number = 0;
  @type("number") castStartedAt: number = 0;

  /**
   * STATEFUL — current mining-rate multiplier in basis points
   * (10000 = 1.0 = baseline, 13000 = +30%, 14500 = +45% with Mining 5
   * formula, etc.). Mineral Tap writes 13000 + sets a 30s expiry tracked
   * server-side; expiry rolls back to 10000.
   *
   * Stored on Spellbook (not Cube) because it's a cast-driven buff state,
   * not a baseline stat — keeps the derived-stats compute path from
   * accidentally rebuilding it. Read by the gather-tick handler.
   *
   * uint16 caps at 65535 = ~6.5× — comfortable headroom over KTS's
   * 1.0–1.45× range for utility-buff stacks.
   */
  @type("uint16") miningRateBp: number = 10000;

  /**
   * TRANSIENT — server-ms when the active mining-rate buff expires; 0 = none.
   * Written by Mineral Tap effect handler; cleared by the 1Hz expiry sweep
   * in HelloRoom. Read by the gather pipeline together with `miningRateBp`.
   */
  @type("number") miningBuffExpiresAt: number = 0;

  /**
   * TRANSIENT — active buff instances keyed by buffId. The buff pipeline
   * writes on apply and deletes on expiry. Clients read for HUD icon rows +
   * duration arcs (future tier — shape is stable now so that tier is
   * render-only with no protocol change).
   *
   * Lives on Spellbook (not Cube) because Cube is at the
   * @colyseus/schema 64-field cap (same constraint that pushed `mana` here).
   * MapSchema<BuffInstance> costs 1 Cube field via `cube.spellbook`.
   */
  @type({ map: BuffInstance }) buffs = new MapSchema<BuffInstance>();

  /**
   * TRANSIENT — active toggle abilities keyed by abilityId. Value is the
   * server-ms auto-off deadline; 0 = indefinite (drains until re-press,
   * break event, or death). The toggle pipeline writes on enable + clears
   * on disable; the per-tick driver checks `value > 0 && now >= value` for
   * auto-off. Clients read for HUD "lit" state on the ability bar.
   *
   * Refresh-on-reapply: re-pressing a toggle while it's ON disables it
   * (re-press is the off path); re-pressing while OFF (or after auto-off /
   * break) re-enables. Stack semantics never apply — at most one entry per
   * ability id.
   *
   * MapSchema (not parallel arrays) keeps the wire diff small: only the
   * specific toggle whose state changed deltas to clients.
   */
  @type({ map: "number" }) toggles = new MapSchema<number>();

  /**
   * TRANSIENT — channel-tick accumulator (server-ms since the last period
   * fired). Written by the channel tick when accumulated >= periodMs; reset
   * to (accum - periodMs) so leftover sub-period drift carries forward into
   * the next period. Read by the same tick driver only.
   *
   * Lives on Spellbook because it's per-cube channel state; valid only when
   * `castStage === Channeling`. Cleared on channel cancel + death-reset.
   */
  @type("uint16") channelPeriodAccumMs: number = 0;

  /**
   * TRANSIENT — number of channel ticks yet to fire on the active channel.
   * Initialized at startChannel as `floor(durationMs / periodMs)`; decrements
   * by 1 each time a tick fires; channel ends when this hits 0. Cleared on
   * channel cancel + death-reset. Valid only when `castStage === Channeling`.
   *
   * uint16 caps at 65535 — covers any conceivable channel (a 4-min channel
   * at 100ms cadence is 2400 ticks).
   */
  @type("uint16") channelTicksRemaining: number = 0;

  /**
   * TRANSIENT — current mana, written by the regen tick + cast cost debits.
   * Recomputed to clamp into [0, manaMax] every regen tick. Refilled to
   * manaMax on death (per spec §"Combat-resolver interactions").
   *
   * Lives on Spellbook (not Cube) because Cube already sits at
   * @colyseus/schema 3.x's 64-field-per-class cap; the DERIVED `manaMax`,
   * `manaRegen` siblings stay on Cube next to other recomputeDerivedStats
   * outputs, while this TRANSIENT meter lives in its bag — same split as
   * `cube.hp`/`hpMax` (Cube) vs `cube.survival.warmth`/`hunger`/`stamina`
   * (SurvivalBag).
   *
   * Default 80 is a stub — the first `recomputeDerivedStats` call will
   * clamp into `[0, manaMax = INT × 4]` (~72 at base INT=18).
   */
  @type("uint16") mana: number = 80;

  /**
   * TRANSIENT — aggregate movement-speed modifier in basis points
   * (10000 = ×1.0 baseline; -7500 = -75% slow; +2000 = +20% haste). Sums
   * the contributions of every active slow/haste buff via the buff
   * stat-mod path — `applyStatMods` adds the delta on apply and
   * `revertStatMods` subtracts on remove, so stacking is additive and
   * cleanly reverses when any contributor expires.
   *
   * Read by `effectiveMoveSpeed` in `movement/multipliers.ts`, which
   * folds in `(1 + speedMultBp / 10000)` after the registered behavior-id
   * multipliers and clamps to a -80% floor (≥ 0.20×). The clamp lives at
   * read-time, not on the field itself, so two stacked -75% slows still
   * remain expressible as -150% — they just clip to the floor when read.
   *
   * Lives on Spellbook (not Cube) because Cube is at the
   * @colyseus/schema 64-field cap. Functionally a "*Mod" field — same
   * shape as `armorMod` / `sArmorMod` / `armorPenMod` on Cube — but the
   * stat-mod plumbing in `buffs/stat-mod.ts` routes this single key to
   * `cube.spellbook.speedMultBp` so the BuffDef.statMods authoring
   * surface stays uniform.
   *
   * Tier 9 (slow primitive) customers: PlantTreeSlowBuff (Plant Tree
   * on-place 0.65s near-freeze). Future Tier 9 customers: Black Hole
   * zone slow, Clairvoyance slow, sapling-zone slow, Warcry MS pulse
   * (haste).
   */
  @type("int16") speedMultBp: number = 0;

  /**
   * TRANSIENT — aggregate attack-speed modifier in percentage points
   * (0 = baseline; +25 = +25% haste; -25 = -25% slow). Sums the
   * contributions of every active attack-speed-modifying buff via the
   * buff stat-mod path — `applyStatMods` adds the delta on apply and
   * `revertStatMods` subtracts on remove, so stacking is additive and
   * cleanly reverses when any contributor expires.
   *
   * Lives on Spellbook (not Cube) because Cube is at the
   * @colyseus/schema 64-field cap. Same routing precedent as
   * `speedMultBp` — the BuffDef.statMods authoring surface stays
   * uniform; `buffs/stat-mod.ts` special-cases this key to write through
   * `cube.spellbook`.
   *
   * int8 range (-128..127) is plenty — Black Hole's v1 value is -25,
   * and we don't anticipate stacked AS slows/hastes pushing past
   * ±100% in jam scope.
   *
   * Tier 9 customer: BlackHoleSlowBuff (-25 attack speed alongside
   * its -50% MS slow).
   */
  @type("int8") attackSpeedMod: number = 0;
}

/**
 * Cast-stage discriminator values for `Spellbook.castStage`. Use these
 * constants instead of magic numbers at all read sites.
 */
export const CastStage = {
  Idle:        0,
  Walking:     1,
  Casting:     2,
  Channeling:  3,
} as const;

export type CastStageValue = typeof CastStage[keyof typeof CastStage];
